import {
  getCommentProps,
  getTaskChatMessageAuthorInfo,
  groupWhispers,
  type TaskChatAuthorType,
} from '@frontend/api-client';
import type { TaskComment } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { useEffect, useFeatureIsOn } from '@frontend/react';
import { FlatList, LoadingSpinnerPanel, YStack, useScrollView, type FlatListProps } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import {
  StickyNotesHero,
  TaskChatMessageBubble,
  TaskChatTime,
  TaskChatWhisper,
  WhisperGroup,
} from '@frontend/web/components';
import { TaskChatOptionSheetCard } from '@frontend/web/components/DomainSpecific/Tasks/TaskChatOptionSheetCard';

const STICKY_NOTES = [
  { color: '#d4edda', label: 'Call United about that $200 change fee' },
  { color: '#cff4fc', label: 'Sit on hold with insurance so I don\u2019t have to' },
  { color: '#fde8c8', label: 'Cancel gym membership (been paying since March)' },
  { color: '#d4edda', label: 'Negotiate Comcast bill down' },
  { color: '#e8daef', label: 'Cancel free trial before it charges me' },
  { color: '#cff4fc', label: 'Find a plumber who\u2019s available this century' },
  { color: '#fce4ec', label: 'Get 3 moving quotes' },
  { color: '#fce4ec', label: 'Book a restaurant that doesn\u2019t take online reservations' },
  { color: '#e6ff7b', label: 'Book dentist for whole family' },
  { color: '#fde8c8', label: 'Call pharmacy \u2014 said they\u2019d call back, they didn\u2019t' },
  { color: '#cff4fc', label: 'Find a vet that\u2019s open on Saturdays' },
  { color: '#e8daef', label: 'Return that online order (it\u2019s a whole thing)' },
];

type PreviewTaskDetailChatTabProps = {
  isLoading?: boolean;
  messages: TaskComment[];
  onSignUp: () => void;
  taskId: string;
};

export const PreviewTaskDetailChatTab = ({
  isLoading = false,
  messages,
  onSignUp,
  taskId,
}: PreviewTaskDetailChatTabProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const { scrollRef, scrollToBottom } = useScrollView({ onRenderScroll: 'bottom' });

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const commentsWithDescription = isGameplanPricingEnabled ? [...messages] : groupWhispers([...messages]);

  const renderItem: FlatListProps<TaskComment | TaskComment[]>['renderItem'] = ({ index, item: currentComment }) => {
    if (Array.isArray(currentComment)) {
      if (isGameplanPricingEnabled) return null;
      return (
        <YStack>
          {index === commentsWithDescription.length - 1 && currentComment[0]?.createdAt ? (
            <TaskChatTime time={currentComment[0]?.createdAt} />
          ) : null}
          <WhisperGroup comments={currentComment} contentType="markdown" taskId={taskId} />
        </YStack>
      );
    }
    const { attachments, author, body, createdAt, secureNote, sheets, whisper } = currentComment;
    const previousComment = index ? commentsWithDescription[index + 1] : undefined;
    const previousCommentFlat = Array.isArray(previousComment)
      ? previousComment[previousComment.length - 1]
      : previousComment;

    const { isAuthorVisible, isTimeVisible } = getCommentProps({
      currentComment,
      previousComment: previousCommentFlat,
    });

    const content = (() => {
      if (secureNote) {
        return <TaskChatWhisper body={body} contentType="markdown" createdAt={createdAt} />;
      } else if (sheets?.[0]) {
        const { id, imageUrl, title } = sheets[0];
        return <TaskChatOptionSheetCard className="ml-xl" imageUrl={imageUrl} optionSheetId={id} title={title} />;
      }

      return match(author)
        .with({ type: 'system_bot' }, () =>
          isGameplanPricingEnabled ? (
            <YStack gap="sm" py="md">
              <TaskChatTime time={createdAt} />
              <TaskChatWhisper body={body} contentType="markdown" createdAt={createdAt} whisper={whisper} />
            </YStack>
          ) : (
            <TaskChatWhisper body={body} contentType="markdown" createdAt={createdAt} whisper={whisper} />
          ),
        )
        .otherwise((author) => (
          <YStack gap="sm">
            <TaskChatMessageBubble
              {...currentComment}
              attachments={attachments}
              authorInfo={getTaskChatMessageAuthorInfo({ ...author, type: author.type as TaskChatAuthorType })}
              body={body}
              contentType="markdown"
              isAuthorVisible={isAuthorVisible}
              taskId={taskId}
            />
          </YStack>
        ));
    })();

    return (
      <YStack gapY="md">
        {isTimeVisible && !isGameplanPricingEnabled ? <TaskChatTime time={createdAt} /> : null}
        {content}
      </YStack>
    );
  };

  return (
    <YStack className="relative flex-1 overflow-hidden">
      <FlatList
        ListHeaderComponent={
          <StickyNotesHero
            ctaLabel="Join Duckbill"
            heading="Be your most productive self"
            notes={STICKY_NOTES}
            onClick={onSignUp}
          />
        }
        ListLoadingComponent={<LoadingSpinnerPanel fullScreen />}
        className="px-xl pb-md flex-1"
        contentContainerClassName={safeCss(
          'pt-md pb-xxl gap-y-md max-w-screen-md self-center w-full justify-end flex-1',
        )}
        // eslint-disable-next-line fp/no-mutating-methods
        data={commentsWithDescription.reverse()}
        inverted
        isLoading={isLoading}
        ref={scrollRef}
        renderItem={renderItem}
      />
    </YStack>
  );
};
